import type { TCameraMetric } from "@services/fieldsService";
import { trunc } from "@src/utils/BN";

/**
 * Field-layer identity, shared by the main Fields map (FieldsScreenVM) and the
 * compare pane (CompareVM). Both used to declare their own copy of the view
 * union, the predicates and the camera metric map — add a layer here once.
 */

/** All per-field map views, incl. the potato-camera layers. */
export type TFieldMapView =
  | "border"
  | "yield"
  | "ndvi"
  | "brightness"
  | "moisture"
  | "productivity"
  | "tracks"
  | "potato_count"
  | "stone_percent"
  | "potato_size"
  /**
   * A customer-supplied layer, identified by its id. Dynamic rather than a
   * fixed literal because a field can hold several and the user names them.
   */
  | `custom:${string}`;

export type TSpectralView = "ndvi" | "brightness" | "moisture";
export type TCameraView = "potato_count" | "stone_percent" | "potato_size";

/** NDVI / brightness / moisture share one date list + overlay. */
export const isSpectralView = (v: string): v is TSpectralView =>
  v === "ndvi" || v === "brightness" || v === "moisture";

/**
 * Potato-camera layers reuse the yield heatmap layer/source (same GeoJSON
 * hexagon contract), so only the fetch + legend/stats differ.
 */
export const isCameraView = (v: string): v is TCameraView =>
  v === "potato_count" || v === "stone_percent" || v === "potato_size";

/**
 * Custom layers (customer-supplied maps). Unlike every other layer, these are
 * dynamic: a field can hold several and the user names them, so the view mode
 * carries the layer id rather than being a fixed literal. They render through
 * the same heatmap source as yield/camera — the colours arrive on the features.
 */
export const isCustomView = (v: string): v is `custom:${string}` => v.startsWith("custom:");

/** The layer id inside a `custom:<id>` view mode, or null. */
export const customLayerIdOf = (v: string): string | null =>
  isCustomView(v) ? v.slice("custom:".length) : null;

/** Map camera views to the backend metric param. */
export const CAMERA_METRIC_BY_VIEW: Record<string, TCameraMetric> = {
  potato_count: "count",
  stone_percent: "stone_percent",
  potato_size: "mean_size",
};

/** readFeatures options for the backend's WGS84 GeoJSON on the web-mercator map. */
export const GEOJSON_OPTS = {
  dataProjection: "EPSG:4326",
  featureProjection: "EPSG:3857",
};

/** Units and converters formatCellLabel needs from the screen. */
export type TCellLabelContext = {
  /** Camera mean-size unit from the field's camera stats (default "px"). */
  sizeUnit?: string | null;
  /** The unit the customer named for the active custom layer, if any. */
  customUnit?: string | null;
  convertYield: (value: number) => number;
  locYield: () => string;
  /** lang.fields.cameraUnitPcs */
  pcsLabel: string;
  /** lang.fields.cameraCellStones — "{pct}% stones" */
  stonesLabel: string;
};

/**
 * The text for one heatmap cell under the pointer (hover tooltip) or clicked
 * (pinned cell), or null when the cell has no value for this view. Shared by
 * the main map's hover and click and the compare pane's hover, which carried
 * three copies; a layer without a branch here is silently un-hoverable.
 * Callers keep their own hit-test rules.
 */
export function formatCellLabel(
  feature: { get(key: string): unknown },
  view: string,
  ctx: TCellLabelContext
): string | null {
  if (isCameraView(view)) {
    // Camera hexes carry counts / stone % / mean size.
    const potato = feature.get("potato_count");
    if (potato == null) return null;
    const stonePct = feature.get("stone_percent");
    const meanSize = feature.get("mean_size");
    const parts = [`${potato} ${ctx.pcsLabel}`];
    if (stonePct != null) parts.push(ctx.stonesLabel.replace("{pct}", String(stonePct)));
    if (meanSize != null) parts.push(`${meanSize} ${ctx.sizeUnit ?? "px"}`);
    return parts.join(" · ");
  }
  if (isCustomView(view)) {
    // Customer-supplied values are shown exactly as the file had them, in
    // whatever unit they named — never converted.
    const value = feature.get("value");
    if (value == null) return null;
    const unit = ctx.customUnit ?? "";
    return `${trunc(Number(value), 1)}${unit ? ` ${unit}` : ""}`;
  }
  const yieldValue = feature.get("yield");
  if (yieldValue == null) return null;
  return `${trunc(ctx.convertYield(Number(yieldValue)))} ${ctx.locYield()}`;
}
